// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

// Add this check for environment
const isServer = typeof window == 'undefined';
export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;
  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }
  // Client-side: use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '')
    : process.env.REACT_APP_MARKETPLACE_ROOT_URL;
};
// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];
const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};
const deserialize = str => {
  return transit.read(str, { typeHandlers });
};
const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};
// Modify the request function to handle server-side environment
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;
  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : { body };
  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    // headers: headers || { 'Content-Type': 'application/transit+json' },
    headers: {
      ...(!!headers
        ? Object.keys(headers).length > 1
          ? headers
          : {}
        : { 'Content-Type': 'application/transit+json' }),
    },
    ...bodyMaybe,
    ...rest,
  };
  // Use axios for server-side requests
  if (isServer) {
    const axios = require('axios');
    return axios({
      url,
      method: options.method || 'POST',
      data: bodyMaybe.body,
      headers: fetchOptions.headers,
    })
      .then(response => response.data)
      .catch(error => {
        if (error.response) {
          throw error.response.data;
        }
        throw error;
      });
  }
  // Separate response handling logic
  const handleResponse = res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;
    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  };
  // Client-side fetch
  return window.fetch(url, fetchOptions).then(handleResponse);
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// integration apis 

export const fetchFeatureContractorsApi = body => {
  return post('/api/featured-contractor', body);
};

// Fetch business categories from the local API endpoint
export const fetchBusinessCategoriesApi = (body) => {
  return post('/api/business-categories', body);
};


// <<<<<<<<<<<<<<<<<< Supabase apis >>>>>>>>>>>>>>
export const fetchAllSubscriptionsApi = (body = {}) => {
  return post('/api/all-subscriptions', body);
};


export const fetchUserSubscriptionsApi = (body = {}) => {
  return post('/api/user-subscriptions', body);
};

export const fetchAllTokenpackageApi = (body = {}) => {
  return post('/api/all-tokenPackages', body);
};


// Send endorsement email from the local API endpoint
export const sendEndorsementEmailApi = (body) => {
  return post('/api/send-endorsement-email', body);
};

// Insert endorsement request into the database
export const insertEndorsementRequestApi = (body) => {
  return post('/api/insert-endorsement-request', body);
};

// Update endorsement request with endorsement data
export const updateEndorsementRequestApi = (body) => {
  return post('/api/update-endorsement-request', body);
};

// Fetch endorsement requests for a user
export const fetchEndorsementRequestsApi = (body) => {
  return post('/api/fetch-endorsement-requests', body);
};

// Stripe subscription APIs
export const createSubscriptionApi = body => {
  return post('/api/subscription/create', body);
};

export const updateSubscriptionApi = body => {
  return post('/api/subscription/update', body);
};

export const cancelSubscriptionApi = body => {
  return post('/api/subscription/cancel', body);
};

export const getSubscriptionApi = body => {
  return post('/api/subscription/get', body);
};

// Stripe webhook API
export const stripeWebhookApi = body => {
  return post('/api/stripe/webhooks', body);
};

// algolia
export const createSearchDataApi = body => {
  return post('/api/createSearchData', body);
};
export const updateSearchDataApi = body => {
  return post('/api/updateSearchData', body);
};
export const deleteSearchDataApi = body => {
  return post('/api/deleteSearchData', body);
};

export const createPaymentIntentApi = body => {
  return post('/api/create-payment-intent', body);
};

export const addAttachmentFilesApi = body => {
  return post('/api/addAttachments', body);
};

export const getCategoryTokensApi = body => {
  return post('/api/getCategoryTokens', body)
};

export const addTokensUserWalletApi = body => {
  return post('/api/addTokens', body)
};

export const updateUserApi = body => {
  return post('/api/updateUserData', body)
};

export const addBidIdsOfUsersApi = body => {
  return post('/api/updateUserBidData', body)
};

export const getUserBidIdsApi = body => {
  return post('/api/getUserBidData', body)
};

export const getUserTransactionCountApi = body => {
  return post('/api/getUserTransactionCount', body)
};

export const fetchUserByIdApi = body => {
  return post('/api/fetchUserById', body)
};

export const addBidIdPerListingApi = body => {
  return post('/api/addBidIdPerListing', body);
};

export const getBidIdsPerListingApi = body => {
  return post('/api/getBidIdsPerListing', body);
};

export const removeBidIdApi = body => {
  return post('/api/removeBidId', body);
};

export const updateTransactionViewedApi = body => {
  return post('/api/updateTransactionViewed', body);
};

export const createUserInPipedriveApi = body => {
  return post('/api/createUserInPipedrive', body);
};

export const updateUserInPipedriveApi = body => {
  return post('/api/updateUserInPipedrive', body);
};

export const createProjectDealsInPipedriveApi = body => {
  return post('/api/createProjectDealsInPipedrive', body);
};

export const updateProjectDealsInPipedriveApi = body => {
  return post('/api/updateProjectDealsInPipedrive', body);
};

export const getStageIdsInPipedriveApi = body => {
  return post('/api/getStageIdsInPipedrive', body);
};

export const addBidActivityApi = body => {
  return post('/api/addBidActivity', body);
};

export const updateBidActivityApi = body => {
  return post('/api/updateBidActivity', body);
};
// Fetch endorsement requests for a user
export const fetchEndorsementPendingRequestsApi = (body) => {
  return post('/api/fetch-endorsement-pending-requests', body);
};

// Fetch endorsement requests for a user
export const fetchEndorsementbyEmailApi = (body) => {
  return post('/api/fetch-user-endorsement', body);
};